import React, { useCallback, useEffect, useState } from "react";

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import * as Lucide from "lucide-react";

import { renameFileToPreventDuplicatedName } from "@/components/helpers/string-utils";
import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { FileType, FileUpload } from "@/components/pieces/FileUpload";
import { CircularProgress } from "@/components/pieces/loading/CircularProgress";
import { formatBytes } from "@/components/pieces/roleplays/utils";
import { Button } from "@/components/ui/button";
import { UploadedFile, uploadFileWithProgress } from "@/lib/upload-file";

type UploadingFile = {
  file: File;
  abort: () => void;
  progress: number;
};
type FileStatus = { type: "success" } | { type: "fail"; error: unknown };

export function FileList(props: {
  files: UploadedFile[];
  /** `undefined` means all file types are accepted  */
  acceptedFileTypes: FileType[] | undefined;
  /** If `true`, newly added file will be marked as private and not publicly readable. */
  isPrivate: boolean;
  onSort: (files: UploadedFile[]) => void;
  onAdd: (file: UploadedFile) => void;
  onRemove: (fileId: string) => void;
}) {
  const [invalidFileTypeError, setSInvalidFileTypeError] = useState<{
    invalidFile: File;
  } | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<
    Record</* File name */ string, UploadingFile | null>
  >({});

  const [fileStatuses, setUploadedFileStatuses] = useState<
    Record</* File name */ string, FileStatus | undefined>
  >({});

  const [fileToBeReuploaded, setFileToBeReuploaded] = useState<File | null>(
    null,
  );

  useEffect(() => {
    if (fileToBeReuploaded) {
      onFileUpload(fileToBeReuploaded);
      setFileToBeReuploaded(null);
    }
  }, [uploadingFiles, fileToBeReuploaded]);

  const setUploadingFile = (
    filename: string,
    uploadingFile: UploadingFile | null,
  ) => {
    setUploadingFiles((uploadingFiles) => ({
      ...uploadingFiles,
      [filename]: uploadingFile,
    }));
  };

  /**
   * This function is created so that
   * when the files uploaded too quickly,
   * the progress circle will also animate towards 100 first
   * before disappearing.
   */
  const completeUploading = useCallback(
    (uploadedFile: UploadedFile, callback: () => void) => {
      setUploadingFiles((uploadingFiles) => {
        const uploadingFile = uploadingFiles[uploadedFile.name];
        return {
          ...uploadingFiles,
          [uploadedFile.name]: uploadingFile
            ? {
                ...uploadingFile,
                progress: 100,
              }
            : null,
        };
      });
      setTimeout(() => {
        setUploadingFile(uploadedFile.name, null);
        setUploadedFileStatuses((statuses) => ({
          ...statuses,
          [uploadedFile.name]: { type: "success" },
        }));

        // Clear the success status after 2 seconds
        setTimeout(() => {
          setUploadedFileStatuses((statuses) => ({
            ...statuses,
            [uploadedFile.name]: undefined,
          }));
        }, 2000);
        callback();
      }, 200);
    },
    [],
  );

  const onFileUploaded = useCallback(
    (file: UploadedFile) => completeUploading(file, () => props.onAdd(file)),
    [props.files],
  );

  const onDrop = useCallback<React.DragEventHandler>(async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    if (event.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(event.dataTransfer.files);
      const { acceptedFileTypes } = props;
      const invalidFile = acceptedFileTypes
        ? droppedFiles.find(
            (file) => !acceptedFileTypes.includes(file.type as FileType),
          )
        : undefined;
      if (invalidFile) {
        setSInvalidFileTypeError({ invalidFile });
        return;
      }

      droppedFiles.map(onFileUpload);

      // Clear the drag data
      event.dataTransfer.clearData();
    }
  }, []);

  const onDragOver = useCallback<React.DragEventHandler>((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const onDragEnter = useCallback<React.DragEventHandler>((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const onDragLeave = useCallback<React.DragEventHandler>((event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  }, []);

  const onFileUpload = async (raw: File) => {
    setSInvalidFileTypeError(null);
    const newName = renameFileToPreventDuplicatedName(raw.name, [
      ...props.files.map((file) => file.name),
      ...Object.values(uploadingFiles).map((file) => file?.file.name ?? ""),
    ]);
    const renamedFile = new File([raw], newName, {
      type: raw.type,
      lastModified: raw.lastModified,
    });
    const { promise, abort } = uploadFileWithProgress({
      isPrivate: props.isPrivate,
      file: renamedFile,
      onProgress: (progress) =>
        setUploadingFiles((uploadingFiles) => {
          const uploadingFile = uploadingFiles[newName];
          return {
            ...uploadingFiles,
            [newName]: uploadingFile ? { ...uploadingFile, progress } : null,
          };
        }),
    });
    setUploadingFile(newName, {
      file: renamedFile,
      progress: 0,
      abort: () => {
        abort();
        setUploadingFile(newName, null);
      },
    });
    try {
      const file = await promise;
      onFileUploaded(file);
      return file;
    } catch (error) {
      console.log(error);
      setUploadedFileStatuses((statuses) => ({
        ...statuses,
        [newName]: { type: "fail", error },
      }));
    }
  };

  const onFilesUpload = async (files: File[]) =>
    (await Promise.all(files.map(onFileUpload))).flatMap((x) => (x ? [x] : []));

  return (
    <FileUpload
      multiple
      acceptedFileTypes={props.acceptedFileTypes}
      onFilesUpload={onFilesUpload}
    >
      {(onClick) => (
        <div className="grid">
          <div
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onClick={onClick}
            color="#B0B0B0"
            className={`text-sm ${
              dragActive ? "bg-[#D4E6FC]" : "bg-white"
            } border border-dashed border-[var(--CTA,#3788EE)] grid place-items-center rounded-md py-9 cursor-pointer gap-2`}
          >
            {/*
            Setting `pointerEvents` of all child elements to `none` is necessary
            for Drag to work properly, otherwise dragging over a child element
            would cause `onDragLeave` to be fired unexpectedly.
            See more at https://stackoverflow.com/a/51176856
            */}
            <div className="pointer-events-none">
              {dragActive ? (
                <span style={{ color: "#3788EE" }}>Drop your file</span>
              ) : (
                <span>
                  Drop your files here or{" "}
                  <span style={{ color: "#3788EE" }}>Choose file</span>
                </span>
              )}
            </div>
            <div className="pointer-events-none">
              {props.acceptedFileTypes && invalidFileTypeError && (
                <div
                  className="text-xs px-4 text-center"
                  style={{ color: "#C61D1D" }}
                >
                  <div>
                    The file type of "{invalidFileTypeError.invalidFile.name}"
                    is unsupported.
                  </div>
                  <div>
                    Please upload{" "}
                    {props.acceptedFileTypes.length === 1
                      ? "a"
                      : "one of these file types:"}{" "}
                    {props.acceptedFileTypes.map(formatFileType).join(", ")}{" "}
                    instead.
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="h-px bg-divider w-full my-4" />
          {Object.values(uploadingFiles).filter((x) => x).length > 0 && (
            <div
              className="grid gap-4"
              style={{ marginBottom: props.files.length > 0 ? 16 : 0 }}
            >
              {Object.values(uploadingFiles).map((uploadingFile) => {
                if (!uploadingFile) return null;
                return (
                  <UploadingFileView
                    key={uploadingFile.file.name}
                    reuploadFile={() => {
                      setUploadedFileStatuses((statuses) => ({
                        ...statuses,
                        [uploadingFile.file.name]: undefined,
                      }));
                      setUploadingFiles((files) => ({
                        ...files,
                        [uploadingFile.file.name]: null,
                      }));
                      setFileToBeReuploaded(uploadingFile.file);
                    }}
                    fileStatus={fileStatuses[uploadingFile.file.name]}
                    fileName={uploadingFile.file.name}
                    fileSize={uploadingFile.file.size}
                    progress={uploadingFile.progress}
                    onAbort={uploadingFile.abort}
                  />
                );
              })}
            </div>
          )}
          <UploadedFilesView
            files={props.files}
            fileStatuses={fileStatuses}
            onDownload={(url) => window.open(url, "_blank")}
            onRemove={props.onRemove}
            onChange={props.onSort}
          />
        </div>
      )}
    </FileUpload>
  );
}

export function UploadedFilesView(props: {
  files: UploadedFile[];
  fileStatuses: Record<string, FileStatus | undefined>;
  onRemove: (fileId: string) => void;
  onDownload: (url: string) => void;
  onChange: (files: UploadedFile[]) => void;
}) {
  return (
    <DragSort
      gap={16}
      items={props.files}
      setItems={props.onChange}
      renderItem={(
        listeners: SyntheticListenerMap | undefined,
        file: UploadedFile,
      ) => (
        <UploadedFileView
          listeners={listeners}
          fileStatus={props.fileStatuses[file.name]}
          file={file}
          onDownload={props.onDownload}
          onDelete={() => props.onRemove(file.id)}
        />
      )}
    />
  );
}

const SortableItem = ({
  id,
  renderItem,
}: {
  id: string;
  renderItem: (listeners: SyntheticListenerMap | undefined) => React.ReactNode;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {renderItem(listeners)}
    </div>
  );
};

function DragSort<T extends { id: string }>(props: {
  gap: number;
  items: T[];
  setItems: (items: T[]) => void;
  renderItem: (
    listeners: SyntheticListenerMap | undefined,
    item: T,
  ) => React.ReactNode;
}) {
  const { items, setItems } = props;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    if (active.id !== over.id) {
      const oldIndex = items.findIndex(
        (item) => item.id === active.id.toString(),
      );
      const newIndex = items.findIndex(
        (item) => item.id === over.id.toString(),
      );

      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="grid" style={{ gap: props.gap }}>
          {items.map((item) => (
            <SortableItem
              key={item.id}
              id={item.id}
              renderItem={(listeners) => props.renderItem(listeners, item)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function UploadedFileView(props: {
  listeners: SyntheticListenerMap | undefined;
  file: UploadedFile;
  fileStatus: FileStatus | undefined;
  onDelete: () => void;
  onDownload: (url: string) => void;
}) {
  const actions =
    props.fileStatus?.type === "success" ? (
      <div className="w-17 grid place-items-center">
        <Lucide.CircleCheck size={24} className="text-green-600" />
      </div>
    ) : (
      <div className="grid grid-flow-col gap-2">
        <Tooltip content="Delete" placement="top">
          <Button
            type="button"
            intent="danger"
            onClick={props.onDelete}
            size="sm"
            variant="minimal"
          >
            <Lucide.Trash2 size={24} />
          </Button>
        </Tooltip>
        <Tooltip content="Download" placement="top">
          <Button
            type="button"
            intent="secondary"
            onClick={() => props.onDownload(props.file.url)}
            size="sm"
            variant="minimal"
          >
            <Lucide.Download size={24} />
          </Button>
        </Tooltip>
      </div>
    );

  const bottomRight =
    props.fileStatus?.type === "success"
      ? () => <div className="italic text-[12px] text-green-600">Completed</div>
      : null;

  return (
    <div className="grid grid-cols-[auto_1fr] gap-1 items-center">
      <div {...props.listeners}>
        <Lucide.GripVertical
          className="cursor-move"
          size={24}
          color="#E2E2E2"
        />
      </div>
      <div className="grid grid-flow-col grid-cols-[1fr_auto] justify-start items-center gap-4 p-4 border rounded-md border-divider bg-white">
        <FileInfo
          name={props.file.name}
          fileSize={props.file.size}
          bottomRight={bottomRight}
        />
        {actions}
      </div>
    </div>
  );
}

function UploadingFileView(props: {
  fileName: string;
  fileStatus: FileStatus | undefined;
  fileSize: number;
  progress: number;
  reuploadFile: () => void;
  onAbort: () => void;
}) {
  const actions =
    props.fileStatus?.type === "fail" ? (
      <div className="grid place-items-center w-17">
        <Tooltip content="Remove" placement="top">
          <Button
            intent="danger"
            onClick={props.onAbort}
            size="sm"
            variant="minimal"
          >
            <Lucide.Trash2 size={24} />
          </Button>
        </Tooltip>
      </div>
    ) : (
      <Tooltip content="Cancel Upload" placement="top">
        <div className="grid w-[68px] place-items-center">
          <div className="row-1 col-1">
            <CircularProgress
              progress={props.progress}
              radius={10}
              strokeWidth={2}
            />
          </div>
          <Lucide.X
            onClick={props.onAbort}
            size={14}
            className="row-1 col-1 text-[#778397] cursor-pointer"
          />
        </div>
      </Tooltip>
    );

  return (
    <div className="grid grid-cols-[1fr_auto] justify-start items-center gap-4 p-4 border rounded-md border-divider bg-white ml-7">
      <FileInfo
        name={props.fileName}
        fileSize={props.fileSize}
        bottomRight={() =>
          props.fileStatus?.type === "fail" ? (
            <div
              onClick={props.reuploadFile}
              className="italic text-xs text-red-700 grid items-center gap-1 grid-flow-col justify-start cursor-pointer"
            >
              Upload failed. Tap to retry
              <span>
                <Lucide.RefreshCcw size={14} />
              </span>
            </div>
          ) : (
            <div className="italic text-xs text-blue-600">
              {props.progress >= 100 ? (
                <FinishingUpload />
              ) : (
                <span>Uploading {props.progress.toFixed(0)}% </span>
              )}
            </div>
          )
        }
      />
      {actions}
    </div>
  );
}
function FinishingUpload() {
  const [counter, setCounter] = useState(0);
  const MAX_DOT_COUNT = 5;
  useEffect(() => {
    const timer = setInterval(() => {
      setCounter((counter) => (counter + 1) % MAX_DOT_COUNT);
    }, 500);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return <div>Finishing upload {".".repeat(counter + 1)}</div>;
}
function FileInfo(props: {
  name: string;
  fileSize: number;
  bottomRight: (() => React.ReactNode) | null;
}) {
  return (
    <div className="grid grid-flow-col gap-2 grid-cols-[auto_1fr]">
      <div className="h-8 w-8 rounded bg-blue-600 grid place-items-center">
        <Lucide.File className="text-white" size={20} />
      </div>
      <div className="grid">
        <div className="text-xs text-black font-bold whitespace-nowrap overflow-hidden text-ellipsis">
          {props.name}
        </div>
        <div className="grid grid-flow-col grid-cols-[auto_auto_auto_1fr] justify-start gap-1 text-gray-500 text-xs">
          {props.bottomRight ? (
            props.bottomRight()
          ) : (
            <>
              <div>
                {props.name.split(".").at(-1)?.toUpperCase() ??
                  "Unknown File Type"}
              </div>
              <div>・</div>
              <div>{formatBytes(props.fileSize)}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function formatFileType(fileType: string): string {
  return fileType.split("/")[1]?.toUpperCase() ?? fileType;
}
